import type { SessionEntry, SessionUser, TwoFactorStatusResponse } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type FormEvent, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { STR } from '../i18n';
import {
  ApiError,
  disableTwoFactor,
  getMyTwoFactorStatus,
  listMySessions,
  regenerateTwoFactorRecoveryCodes,
  signOutMyOtherSessions,
  signOutMySession,
} from '../lib/api';
import { brandGlyph, useBranding } from '../lib/branding';
import { distinctApps } from '../lib/devices';
import { formatDate, relativeTime } from '../lib/format';
import { queryKeys } from '../lib/queryKeys';
import { landingPath, surfaceById } from '../lib/surfaces';
import { showToast } from '../lib/toast';
import { SlotBrandMark } from '../ui/BrandMark';
import { Modal, ModalForm, ModalParagraph, ModalProse } from '../ui/Modal';
import { Callout, Chip, Skeleton } from '../ui/Primitives';
import { fieldLabelStyle, modalButtonStyle, quietButtonStyle, textFieldStyle } from '../ui/styles';
import { RecoveryCodesPanel } from '../ui/RecoveryCodes';

/**
 * SECURITY — the self-service two-step section, in the launcher's brand-panel shell and
 * reached from its account menu, per the design of record. It answers only for the
 * signed-in person: the status endpoint takes no user parameter, and there is nothing here
 * an administrator could point at somebody else.
 *
 * BOTH mutations here — regenerate and turn off — carry the account PASSWORD in the body:
 * the server's contract, because a hijacked session does not know the first factor. The
 * dialogs therefore collect it, and a wrong password is the server's own plain sentence.
 *
 * The Active sessions card is the demo's second section, now live: every live device for
 * the signed-in person, same no-user-parameter self-only rule as the two-step card above.
 */

/** `.sess-note` — the honesty copy plus "Sign out everywhere else", above the row list. */
const sessionsNoteStyle: CSSProperties = {
  padding: '12px 18px',
  borderBottom: '1px solid var(--border)',
  background: 'var(--surface-2)',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  flexWrap: 'wrap',
};

/** `.sess-single` — appended below the (single) row when there is nothing else to sign out. */
const sessionSingleStyle: CSSProperties = {
  padding: '14px 18px',
  fontSize: 12.5,
  borderTop: '1px solid var(--border)',
  background: 'var(--surface-2)',
};

export function SecurityPage({ user }: { user: SessionUser }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const branding = useBranding();
  const data = branding.data;

  const glyph = data === undefined ? '' : brandGlyph(data);
  const tagline = data?.tagline.trim() ?? '';
  const taglineSub = data?.taglineSub.trim() ?? '';

  const statusQuery = useQuery({
    queryKey: queryKeys.my2fa,
    queryFn: getMyTwoFactorStatus,
  });

  const [dialog, setDialog] = useState<'regenerate' | 'turnOff' | null>(null);
  /** THE ONE SANCTIONED HOME for a regenerated batch — component state, gone on dismissal. */
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  const sessionsQuery = useQuery({ queryKey: queryKeys.mySessions, queryFn: listMySessions });
  const mySessions = sessionsQuery.data?.sessions ?? [];
  const otherDeviceCount = mySessions.filter((entry) => !entry.current).length;
  const [othersModalOpen, setOthersModalOpen] = useState(false);

  const signOutDeviceMutation = useMutation({
    mutationFn: (sessionId: string) => signOutMySession(sessionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.mySessions });
      showToast(STR.toast.deviceSignedOut);
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const signOutOthersMutation = useMutation({
    mutationFn: signOutMyOtherSessions,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.mySessions });
      setOthersModalOpen(false);
      showToast(STR.toast.otherSessionsSignedOut, 'warn');
    },
    onError: (error) => {
      setOthersModalOpen(false);
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  let card: ReactNode;
  if (statusQuery.isLoading) {
    card = <StatusSkeleton />;
  } else if (statusQuery.isError || statusQuery.data === undefined) {
    card = (
      <div style={{ padding: '16px 18px' }}>
        <Callout
          tone="danger"
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            onClick={() => {
              void statusQuery.refetch();
            }}
          >
            {STR.common.tryAgain}
          </button>
        </Callout>
      </div>
    );
  } else if (statusQuery.data.enabled) {
    card = (
      <EnabledRows
        status={statusQuery.data}
        onRegenerate={() => {
          setDialog('regenerate');
        }}
        onTurnOff={() => {
          setDialog('turnOff');
        }}
      />
    );
  } else {
    card = (
      <div className="sec-row">
        <div className="grow">
          <div className="t">
            <span className="nm">{STR.twoFactor.security.rowTitle}</span>
            <Chip tone="neutral">{STR.twoFactor.security.chipOff}</Chip>
          </div>
          <div className="note">{STR.twoFactor.security.offNote}</div>
        </div>
        <button
          type="button"
          className="prim sec-act"
          onClick={() => {
            navigate(surfaceById('twoFactorSetup').path);
          }}
        >
          {STR.twoFactor.security.turnOn}
        </button>
      </div>
    );
  }

  return (
    <main>
      <div className="c-grid">
        <aside className="c-brand">
          <div className="brand-row">
            <SlotBrandMark logoUrl={data?.logoUrl ?? null} glyph={glyph} />
            {data === undefined ? (
              <Skeleton style={{ height: 12, width: 120, borderRadius: 5 }} />
            ) : (
              <span className="brand-name">{data.name}</span>
            )}
          </div>
          {tagline === '' ? null : <p className="c-line">{tagline}</p>}
          {taglineSub === '' ? null : <p className="c-sub">{taglineSub}</p>}
          <div className="c-glyph" aria-hidden="true">
            {glyph}
          </div>
        </aside>

        <div className="c-main">
          <div>
            <button
              type="button"
              className="tbtn back-btn"
              onClick={() => {
                navigate(landingPath());
              }}
            >
              {STR.twoFactor.security.back}
            </button>
            <h1 className="greet-h" style={{ marginTop: 12 }}>
              {STR.twoFactor.security.title}
            </h1>
            <p className="greet-p">{STR.twoFactor.security.subtitle(user.name, user.email)}</p>
          </div>

          <div className="c-sec">
            <span className="mlab">{STR.twoFactor.security.sectionLabel}</span>
            <div className="cardx">{card}</div>
          </div>

          <div className="c-sec">
            <span className="mlab">{STR.sessions.security.sectionLabel}</span>
            <div className="cardx">
              <div style={sessionsNoteStyle}>
                <span
                  className="tx-subtle"
                  style={{ flex: 1, minWidth: 200, fontSize: 12, lineHeight: 1.55, textWrap: 'pretty' }}
                >
                  {STR.sessions.security.honesty}
                </span>
                {sessionsQuery.isLoading || otherDeviceCount === 0 ? null : (
                  <button
                    type="button"
                    className="ghost sec-act"
                    onClick={() => {
                      setOthersModalOpen(true);
                    }}
                  >
                    {STR.sessions.security.signOutOthers}
                  </button>
                )}
              </div>

              {sessionsQuery.isLoading ? <SessionListSkeleton /> : null}

              {!sessionsQuery.isLoading && (sessionsQuery.isError || sessionsQuery.data === undefined) ? (
                <div style={{ padding: '16px 18px' }}>
                  <Callout
                    tone="danger"
                    role="alert"
                    style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
                  >
                    <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
                    <button
                      type="button"
                      className="tbtn"
                      style={quietButtonStyle}
                      onClick={() => {
                        void sessionsQuery.refetch();
                      }}
                    >
                      {STR.common.tryAgain}
                    </button>
                  </Callout>
                </div>
              ) : null}

              {!sessionsQuery.isLoading && sessionsQuery.data !== undefined ? (
                <>
                  {mySessions.map((entry) => (
                    <SelfSessionRow
                      key={entry.id}
                      session={entry}
                      busy={signOutDeviceMutation.isPending}
                      onSignOut={() => {
                        signOutDeviceMutation.mutate(entry.id);
                      }}
                    />
                  ))}
                  {otherDeviceCount === 0 ? (
                    <div className="tx-subtle" style={sessionSingleStyle}>
                      {STR.sessions.security.singleDevice}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {othersModalOpen ? (
        <Modal
          title={STR.sessions.security.othersModal.title}
          onClose={() => {
            setOthersModalOpen(false);
          }}
          footer={
            <>
              <button
                type="button"
                className="ghost"
                style={modalButtonStyle}
                onClick={() => {
                  setOthersModalOpen(false);
                }}
              >
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={signOutOthersMutation.isPending}
                onClick={() => {
                  signOutOthersMutation.mutate();
                }}
              >
                {STR.sessions.security.othersModal.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.sessions.security.othersModal.body(otherDeviceCount)}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}

      {dialog === 'regenerate' ? (
        <RegenerateModal
          onClose={() => {
            setDialog(null);
          }}
          onCodes={(codes) => {
            setDialog(null);
            setNewCodes(codes);
          }}
        />
      ) : null}

      {dialog === 'turnOff' ? (
        <TurnOffModal
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}

      {newCodes !== null ? (
        <NewCodesModal
          codes={newCodes}
          email={user.email}
          onClose={() => {
            setNewCodes(null);
          }}
        />
      ) : null}
    </main>
  );
}

/** The demo's skeleton rows — a genuine first paint only. */
function StatusSkeleton() {
  return (
    <>
      {[0, 1].map((row) => (
        <div className="sec-row" key={row}>
          <div className="grow">
            <Skeleton style={{ height: 13, width: 220, borderRadius: 5 }} />
            <Skeleton style={{ height: 10, width: 300, borderRadius: 5, marginTop: 8 }} />
          </div>
          <Skeleton style={{ height: 34, width: 130, borderRadius: 8 }} />
        </div>
      ))}
    </>
  );
}

function EnabledRows({
  status,
  onRegenerate,
  onTurnOff,
}: {
  status: TwoFactorStatusResponse;
  onRegenerate: () => void;
  onTurnOff: () => void;
}) {
  return (
    <>
      <div className="sec-row">
        <div className="grow">
          <div className="t">
            <span className="nm">{STR.twoFactor.security.rowTitle}</span>
            <Chip tone="positive">{STR.twoFactor.security.chipOn}</Chip>
          </div>
          <div className="note">
            {status.enrolledAt === null
              ? STR.twoFactor.security.enabledNote
              : STR.twoFactor.security.enrolledNote(formatDate(status.enrolledAt))}
          </div>
        </div>
      </div>
      <div className="sec-row">
        <div className="grow">
          <div className="t">
            <span className="nm">{STR.twoFactor.security.codesRowTitle}</span>
          </div>
          <div className="note">
            {STR.twoFactor.security.codesLeftLine(status.recoveryCodesRemaining)}{' '}
            {STR.twoFactor.security.codesLeftSuffix}
          </div>
        </div>
        <button type="button" className="ghost sec-act" onClick={onRegenerate}>
          {STR.twoFactor.security.regenerate}
        </button>
      </div>
      <div className="sec-row">
        <div className="grow">
          <div className="t">
            <span className="nm">{STR.twoFactor.security.turnOffRowTitle}</span>
          </div>
          <div className="note">{STR.twoFactor.security.turnOffNote}</div>
        </div>
        <button type="button" className="ghost sec-act" onClick={onTurnOff}>
          {STR.twoFactor.security.turnOff}
        </button>
      </div>
    </>
  );
}

/** The Active sessions card's own first paint — the demo's `sess-row` skeleton language. */
function SessionListSkeleton() {
  return (
    <>
      {[0, 1].map((row) => (
        <div className="sec-row" key={row}>
          <div className="grow">
            <Skeleton style={{ height: 13, width: 170, borderRadius: 5 }} />
            <Skeleton style={{ height: 10, width: 250, borderRadius: 5, marginTop: 8 }} />
          </div>
          <Skeleton style={{ height: 32, width: 86, borderRadius: 8 }} />
        </div>
      ))}
    </>
  );
}

/** THE META LINE, self-service form: `<address> · <location> · <relative last-active>`,
 *  plus the honest clause when the browser did not identify itself. */
function metaLine(session: SessionEntry): string {
  const parts: string[] = [];
  if (session.sourceAddress !== null) parts.push(session.sourceAddress);
  parts.push(
    session.location === null
      ? STR.sessions.locationUnknown
      : STR.sessions.locationApprox(session.location),
  );
  parts.push(relativeTime(session.lastActiveAt));
  if (session.deviceUnknown) parts.push(STR.sessions.deviceUnknownClause);
  return parts.join(' · ');
}

/**
 * One device row. The current device carries "This device" and no Sign out — ending the
 * session you are standing in is not a thing this row offers, on either surface.
 *
 * THE APPLICATION CHIPS (U6) are the admin Sessions screen's own treatment, reused rather
 * than re-drawn: the same `Chip` primitive, the same `size="sm"`, the same colour source —
 * `apps[].color`, the application's own pill colour resolved server-side on this very read.
 * THE STANDING CARVE-OUT holds: a device with no apps renders NO apps segment at all, which
 * an empty map does by construction — never "0 apps", never a placeholder.
 */
function SelfSessionRow({
  session,
  busy,
  onSignOut,
}: {
  session: SessionEntry;
  busy: boolean;
  onSignOut: () => void;
}) {
  const apps = distinctApps([session]);

  return (
    <div className="sec-row">
      <div className="grow">
        <div className="t">
          <span className={session.deviceUnknown ? 'nm tx-subtle' : 'nm'}>{session.deviceLabel}</span>
          {apps.map((app) => (
            <Chip key={app.id} tone="neutral" swatch={app.color} size="sm">
              {app.name}
            </Chip>
          ))}
          {session.current ? <Chip tone="positive" size="sm">{STR.sessions.thisDevice}</Chip> : null}
        </div>
        <div className="note">{metaLine(session)}</div>
      </div>
      {session.current ? null : (
        <button type="button" className="ghost sec-act" disabled={busy} onClick={onSignOut}>
          {STR.sessions.signOut}
        </button>
      )}
    </div>
  );
}

/**
 * "Regenerate recovery codes?" — the password proof. Success does not close into thin air:
 * the parent swaps this dialog for the shown-once batch, which is the entire point of the
 * call. The server refuses a wrong password with its own plain sentence; a 409 (nothing
 * enrolled to attach codes to) renders the same way.
 */
function RegenerateModal({
  onClose,
  onCodes,
}: {
  onClose: () => void;
  onCodes: (codes: string[]) => void;
}) {
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => regenerateTwoFactorRecoveryCodes({ password }),
    onSuccess: (result) => {
      onCodes(result.recoveryCodes);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFormError(
          error.code === 'rate_limited' ? STR.twoFactor.challenge.rateLimited : error.message,
        );
        return;
      }
      setFormError(STR.errors.generic);
    },
  });

  const busy = mutation.isPending;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (password.trim() === '') {
      setFormError(STR.twoFactor.regenModal.passwordRequired);
      return;
    }
    setFormError(null);
    mutation.mutate();
  }

  return (
    <Modal
      title={STR.twoFactor.regenModal.title}
      lead={STR.twoFactor.regenModal.lead}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="submit"
            form="regenerate-recovery-codes"
            className="prim"
            style={modalButtonStyle}
            disabled={busy}
          >
            {busy ? STR.twoFactor.regenModal.working : STR.twoFactor.regenModal.submit}
          </button>
        </>
      }
    >
      <form id="regenerate-recovery-codes" onSubmit={handleSubmit} noValidate>
        <ModalForm>
          <Callout tone="attention" style={{ fontWeight: 600 }}>
            {STR.twoFactor.regenModal.warning}
          </Callout>
          {formError === null ? null : (
            <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
              {formError}
            </Callout>
          )}
          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.twoFactor.regenModal.passwordLabel}
            </span>
            <input
              className={formError === null ? 'field' : 'field field-over'}
              type="password"
              autoComplete="current-password"
              disabled={busy}
              style={textFieldStyle}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setFormError(null);
              }}
            />
          </label>
        </ModalForm>
      </form>
    </Modal>
  );
}

/**
 * The regenerated batch, through the SAME shown-once treatment as enrolment: the orange
 * finality note, the code grid, copy/download, and an acknowledgement gating Done — with
 * the ambient dismissal paths (Escape, backdrop) sealed until it is ticked, exactly as the
 * application-secret dialog does. Closing is the moment the codes cease to exist here.
 */
function NewCodesModal({
  codes,
  email,
  onClose,
}: {
  codes: string[];
  email: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [acknowledged, setAcknowledged] = useState(false);

  function done() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.my2fa });
    showToast(STR.toast.newRecoveryCodes);
    onClose();
  }

  return (
    <Modal
      title={STR.twoFactor.newCodesModal.title}
      lead={STR.twoFactor.newCodesModal.lead}
      dismissable={acknowledged}
      onClose={done}
      footer={
        <button
          type="button"
          className="prim"
          style={modalButtonStyle}
          disabled={!acknowledged}
          onClick={done}
        >
          {STR.twoFactor.newCodesModal.done}
        </button>
      }
    >
      <ModalForm>
        <Callout tone="attention" style={{ fontWeight: 600 }}>
          {STR.twoFactor.newCodesModal.once}
        </Callout>
        <RecoveryCodesPanel
          codes={codes}
          email={email}
          acknowledged={acknowledged}
          onAcknowledgedChange={setAcknowledged}
        />
      </ModalForm>
    </Modal>
  );
}

/**
 * "Turn off two-step?" — password proof, danger action. Success invalidates the status so
 * the card flips to "Not set up" from the server's own answer, never from a client guess.
 */
function TurnOffModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => disableTwoFactor({ password }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.my2fa });
      showToast(STR.toast.twoStepOff, 'warn');
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFormError(
          error.code === 'rate_limited' ? STR.twoFactor.challenge.rateLimited : error.message,
        );
        return;
      }
      setFormError(STR.errors.generic);
    },
  });

  const busy = mutation.isPending;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (password.trim() === '') {
      setFormError(STR.twoFactor.offModal.passwordRequired);
      return;
    }
    setFormError(null);
    mutation.mutate();
  }

  return (
    <Modal
      title={STR.twoFactor.offModal.title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="submit"
            form="turn-off-two-step"
            className="dbtn"
            style={modalButtonStyle}
            disabled={busy}
          >
            {busy ? STR.twoFactor.offModal.working : STR.twoFactor.offModal.submit}
          </button>
        </>
      }
    >
      <form id="turn-off-two-step" onSubmit={handleSubmit} noValidate>
        <ModalForm>
          <ModalParagraph>{STR.twoFactor.offModal.body}</ModalParagraph>
          <Callout tone="attention" style={{ fontWeight: 600 }}>
            {STR.twoFactor.offModal.warning}
          </Callout>
          {formError === null ? null : (
            <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
              {formError}
            </Callout>
          )}
          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.twoFactor.offModal.passwordLabel}
            </span>
            <input
              className={formError === null ? 'field' : 'field field-over'}
              type="password"
              autoComplete="current-password"
              disabled={busy}
              style={textFieldStyle}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setFormError(null);
              }}
            />
          </label>
        </ModalForm>
      </form>
    </Modal>
  );
}
